import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useRepositoryStore } from '../stores/useRepositoryStore';
import { useSchedules } from '../hooks/useSchedules';
import ScheduleModal from '../components/ScheduleModal';
import { Schedule } from '../types';
import { PageHeader, Card, Button, EmptyState } from '../components/ui';

function formatDaysOfWeek(days: string | undefined): string {
  if (!days) return '';
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayList = days.split(',').map(d => dayNames[parseInt(d)]);
  if (dayList.length === 7) return 'Every day';
  if (dayList.length === 5 && !dayList.includes('Saturday') && !dayList.includes('Sunday'))
    return 'Weekdays';
  if (dayList.length === 2 && dayList.includes('Saturday') && dayList.includes('Sunday'))
    return 'Weekends';
  return dayList.join(', ');
}

function formatScheduleType(schedule: Schedule): string {
  if (schedule.scheduleType === 'ONCE') return 'Custom (One-time)';
  if (schedule.scheduleType === 'WEEKLY') return 'Weekly';
  if (schedule.scheduleType === 'DAILY') return 'Daily';
  return schedule.scheduleType;
}

function getScheduleSummary(schedule: Schedule): string {
  if (schedule.scheduleType === 'ONCE' && schedule.startDate && schedule.endDate) {
    const startDate = new Date(schedule.startDate);
    const endDate = new Date(schedule.endDate);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

    const startStr = startDate.toLocaleDateString(undefined, options);
    const endStr = endDate.toLocaleDateString(undefined, options);

    if (startDate.toDateString() === endDate.toDateString()) {
      return `Active on ${startStr} from ${schedule.startTime} to ${schedule.endTime}`;
    }
    return `Active from ${startStr} ${schedule.startTime} to ${endStr} ${schedule.endTime}`;
  }
  return '';
}

export default function SchedulesPage() {
  const { authStatus } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Use Zustand store instead of useRepositories hook
  const repositories = useRepositoryStore(state => state.repositories);
  const loadRepositories = useRepositoryStore(state => state.loadRepositories);
  const initialLoad = useRepositoryStore(state => state.initialLoad);

  const {
    schedules,
    selectedScheduleRepo,
    setSelectedScheduleRepo,
    loadSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    toggleSchedule,
  } = useSchedules();

  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

  const accessibleRepos = repositories.filter(r => r.isAccessible);

  // Load repositories only if not already loaded (initial load)
  useEffect(() => {
    if (authStatus.authenticated && authStatus.currentInstallation && initialLoad) {
      loadRepositories();
    }
  }, [authStatus.authenticated, authStatus.currentInstallation, initialLoad, loadRepositories]);

  // Set the repo from URL query param, localStorage, or default to first repo
  useEffect(() => {
    if (repositories.length === 0) return;

    const repoParam = searchParams.get('repo');
    if (repoParam) {
      setSelectedScheduleRepo(repoParam);
      localStorage.setItem('yukon:lastSelectedScheduleRepo', repoParam);
      return;
    }

    // Check if currently selected repo is still accessible
    if (selectedScheduleRepo) {
      const isStillAccessible = accessibleRepos.some(
        r => `${r.owner}/${r.repo}` === selectedScheduleRepo
      );
      if (!isStillAccessible) {
        // Selected repo was removed, clear it
        setSelectedScheduleRepo('');
        localStorage.removeItem('yukon:lastSelectedScheduleRepo');
      }
    }

    // Try to load from localStorage
    const lastSelected = localStorage.getItem('yukon:lastSelectedScheduleRepo');
    if (lastSelected && accessibleRepos.some(r => `${r.owner}/${r.repo}` === lastSelected)) {
      setSelectedScheduleRepo(lastSelected);
      return;
    }

    // If lastSelected is not accessible anymore, clear it
    if (lastSelected && !accessibleRepos.some(r => `${r.owner}/${r.repo}` === lastSelected)) {
      localStorage.removeItem('yukon:lastSelectedScheduleRepo');
    }

    // Default to first accessible repo
    if (accessibleRepos.length > 0 && !selectedScheduleRepo) {
      const firstRepo = `${accessibleRepos[0].owner}/${accessibleRepos[0].repo}`;
      setSelectedScheduleRepo(firstRepo);
      localStorage.setItem('yukon:lastSelectedScheduleRepo', firstRepo);
    }
  }, [searchParams, repositories, accessibleRepos, selectedScheduleRepo, setSelectedScheduleRepo]);

  useEffect(() => {
    if (selectedScheduleRepo) {
      const [owner, repo] = selectedScheduleRepo.split('/');
      loadSchedules(owner, repo);
    }
  }, [selectedScheduleRepo, loadSchedules]);

  const handleRepoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRepo = e.target.value;
    setSelectedScheduleRepo(newRepo);
    if (newRepo) {
      localStorage.setItem('yukon:lastSelectedScheduleRepo', newRepo);
    }
  };

  const openCreateModal = () => {
    setEditingSchedule(null);
    setShowModal(true);
  };

  const openEditModal = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSchedule(null);
  };

  const handleSaveSchedule = async (scheduleData: Partial<Schedule>) => {
    const result = editingSchedule
      ? await updateSchedule(editingSchedule.id, scheduleData)
      : await createSchedule(scheduleData);

    if (result.success) {
      showToast('Schedule saved successfully', 'success');
      closeModal();
      if (selectedScheduleRepo) {
        const [owner, repo] = selectedScheduleRepo.split('/');
        await loadSchedules(owner, repo);
      }
    } else {
      showToast(result.error || 'Failed to save schedule', 'error');
    }
  };

  const handleToggleSchedule = async (scheduleId: string, currentState: boolean) => {
    const result = await toggleSchedule(scheduleId, currentState);
    if (result.success) {
      showToast(`Schedule ${!currentState ? 'enabled' : 'disabled'}`, 'success');
      if (selectedScheduleRepo) {
        const [owner, repo] = selectedScheduleRepo.split('/');
        await loadSchedules(owner, repo);
      }
    } else {
      showToast('Failed to update schedule', 'error');
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    const result = await deleteSchedule(scheduleId);
    if (result.success) {
      showToast('Schedule deleted successfully', 'success');
      if (selectedScheduleRepo) {
        const [owner, repo] = selectedScheduleRepo.split('/');
        await loadSchedules(owner, repo);
      }
    } else {
      showToast('Failed to delete schedule', 'error');
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Schedule Management"
        subtitle="Configure automatic freeze and unfreeze schedules for your repositories"
        stats={
          <div className="schedule-header-controls">
            <label htmlFor="scheduleRepoSelect" className="schedule-repo-label">
              Repository:
            </label>
            <select
              id="scheduleRepoSelect"
              className="schedule-repo-select"
              value={selectedScheduleRepo}
              onChange={handleRepoChange}
            >
              <option value="">-- Select a repository --</option>
              {accessibleRepos.map(repo => (
                <option key={`${repo.owner}/${repo.repo}`} value={`${repo.owner}/${repo.repo}`}>
                  {repo.owner}/{repo.repo}
                </option>
              ))}
            </select>
            {selectedScheduleRepo && (
              <Button
                id="createScheduleBtn"
                variant="primary"
                icon="fa-solid fa-plus"
                onClick={openCreateModal}
              >
                New Schedule
              </Button>
            )}
          </div>
        }
      />

      <div className="page-content">
        {/* Schedules Section */}
        {selectedScheduleRepo && (
          <div className="schedules-section">
            <div
              style={{
                marginBottom: '20px',
                padding: '10px 14px',
                backgroundColor: '#fafbfc',
                border: '1px solid #e1e4e8',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#6a737d',
              }}
            >
              <i className="fa-solid fa-lightbulb" style={{ marginRight: '6px', opacity: 0.6 }}></i>
              Tip: Weekly schedules span multiple days. Example: selecting Friday-Monday with 5:00
              PM-9:00 AM creates a weekend freeze (Fri 5pm → Mon 9am). Use custom schedules for
              specific dates like holidays or major releases.
            </div>
            <div id="schedulesContainer" className="schedules-grid">
              {schedules.map(schedule => (
                <div
                  key={schedule.id}
                  className={`schedule-card ${!schedule.enabled ? 'disabled' : ''}`}
                >
                  <div className="schedule-card-header">
                    <h4>{schedule.name || 'Unnamed Schedule'}</h4>
                    <div className="schedule-card-header-bottom">
                      <div
                        className={`schedule-status ${schedule.enabled ? 'enabled' : 'disabled'}`}
                      >
                        <span className="status-dot"></span>
                        <span>{schedule.enabled ? 'Active' : 'Paused'}</span>
                      </div>
                      <div className="schedule-actions">
                        <button
                          className="btn-icon toggle-btn"
                          onClick={() => handleToggleSchedule(schedule.id, schedule.enabled)}
                          title={schedule.enabled ? 'Pause' : 'Activate'}
                        >
                          <i
                            className={`fa-solid ${schedule.enabled ? 'fa-pause' : 'fa-play'}`}
                          ></i>
                        </button>
                        <button
                          className="btn-icon edit-btn"
                          onClick={() => openEditModal(schedule)}
                          title="Edit"
                        >
                          <i className="fa-solid fa-pen"></i>
                        </button>
                        <button
                          className="btn-icon delete-btn"
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          title="Delete"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="schedule-details">
                    {schedule.scheduleType === 'ONCE' && schedule.startDate && schedule.endDate && (
                      <>
                        <div className="detail-row">
                          <div className="detail-icon freeze">
                            <i className="fa-solid fa-snowflake" style={{ color: '#60a5fa' }}></i>
                          </div>
                          <div className="detail-content">
                            <div className="detail-label">Freeze Date & Time</div>
                            <div className="detail-value">
                              {new Date(schedule.startDate).toLocaleDateString()} at{' '}
                              {schedule.startTime}
                            </div>
                          </div>
                        </div>
                        <div className="detail-row">
                          <div className="detail-icon unfreeze">
                            <i
                              className="fa-solid fa-fire-flame-curved"
                              style={{ color: '#f97316' }}
                            ></i>
                          </div>
                          <div className="detail-content">
                            <div className="detail-label">Unfreeze Date & Time</div>
                            <div className="detail-value">
                              {new Date(schedule.endDate).toLocaleDateString()} at{' '}
                              {schedule.endTime}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {schedule.scheduleType !== 'ONCE' && (
                      <>
                        <div className="detail-row">
                          <div className="detail-icon freeze">
                            <i className="fa-solid fa-snowflake" style={{ color: '#60a5fa' }}></i>
                          </div>
                          <div className="detail-content">
                            <div className="detail-label">Freeze Time</div>
                            <div className="detail-value">{schedule.startTime}</div>
                          </div>
                        </div>
                        <div className="detail-row">
                          <div className="detail-icon unfreeze">
                            <i
                              className="fa-solid fa-fire-flame-curved"
                              style={{ color: '#f97316' }}
                            ></i>
                          </div>
                          <div className="detail-content">
                            <div className="detail-label">Unfreeze Time</div>
                            <div className="detail-value">{schedule.endTime}</div>
                          </div>
                        </div>
                      </>
                    )}
                    {schedule.daysOfWeek && (
                      <div className="detail-row">
                        <div className="detail-icon calendar">
                          <i className="fa-solid fa-calendar"></i>
                        </div>
                        <div className="detail-content">
                          <div className="detail-label">Active Days</div>
                          <div className="detail-value">
                            {formatDaysOfWeek(schedule.daysOfWeek)}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="detail-row">
                      <div className="detail-icon clock">
                        <i className="fa-solid fa-clock" style={{ color: '#6B3F8C' }}></i>
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Timezone</div>
                        <div className="detail-value">{schedule.timezone}</div>
                      </div>
                    </div>
                    {schedule.branchName && (
                      <div className="detail-row">
                        <div className="detail-icon branch">
                          <i className="fa-solid fa-code-branch" style={{ color: '#6B3F8C' }}></i>
                        </div>
                        <div className="detail-content">
                          <div className="detail-label">Branch</div>
                          <div className="detail-value">{schedule.branchName}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {schedules.length === 0 && selectedScheduleRepo && (
                <EmptyState
                  icon="fa-regular fa-calendar"
                  title="No schedules configured"
                  description="Create a schedule to automatically freeze and unfreeze this repository"
                />
              )}
            </div>
          </div>
        )}
      </div>

      <ScheduleModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSaveSchedule}
        editingSchedule={editingSchedule}
        selectedRepo={selectedScheduleRepo}
        subscriptionTier={authStatus.currentOrganization?.subscriptionTier}
      />
    </div>
  );
}
